import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertCircle, Loader2, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADE_STYLES = {
  Excellent: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', ring: 'text-emerald-500' },
  Good:      { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    ring: 'text-blue-500' },
  Fair:      { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   ring: 'text-amber-500' },
  Poor:      { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     ring: 'text-red-500' },
  'No Data': { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-600',   ring: 'text-slate-400' },
};

const SIGNAL_LABELS = {
  classification_coverage: 'L1-L4 Coverage',
  field_completeness:      'Field Completeness',
  confidence:              'AI Confidence',
  vocabulary_stability:    'Vocabulary Stability',
  supplier_normalization:  'Supplier Normalization',
  hierarchy_consistency:   'Hierarchy Consistency',
};

export const TrustScoreCard = ({ token }) => {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchScore = async () => {
      try {
        const res = await axios.get(`${API}/data-refinery/trust-score`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setScore(res.data);
      } catch {
        if (mounted) setScore(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchScore();
    const interval = setInterval(fetchScore, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-2 text-sm text-slate-500" data-testid="trust-score-loading">
        <Loader2 size={14} className="animate-spin" /> Computing trust score...
      </div>
    );
  }

  if (!score || score.grade === 'No Data') return null;

  const styles = GRADE_STYLES[score.grade] || GRADE_STYLES.Poor;

  return (
    <div
      className={`${styles.bg} ${styles.border} border rounded-sm overflow-hidden`}
      data-testid="trust-score-card"
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-5 py-4 flex items-center justify-between hover:opacity-90 transition-opacity"
        data-testid="trust-score-toggle"
      >
        <div className="flex items-center gap-4">
          {/* Circular score display */}
          <div className="relative w-16 h-16 flex-shrink-0">
            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="14" fill="none" className="stroke-white/60" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="14" fill="none"
                className={styles.ring}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${(score.score / 100) * 88} 88`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-xl font-heading font-bold ${styles.text}`}>{score.score}</span>
            </div>
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck size={16} className={styles.ring} />
              Spend Data Trust Score
            </h3>
            <p className={`text-sm font-semibold ${styles.text}`}>
              {score.grade}
              <span className="text-xs text-slate-500 font-normal ml-2">
                • {score.total_records.toLocaleString()} records • {score.fully_classified_records.toLocaleString()} fully classified
              </span>
            </p>
          </div>
        </div>
        <span className="text-xs text-slate-500 hover:text-slate-700">
          {expanded ? 'Hide breakdown' : 'View breakdown'}
        </span>
      </button>

      {expanded && (
        <div className="bg-white/70 border-t border-slate-200 px-5 py-4 space-y-3" data-testid="trust-score-breakdown">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="trust-score-signals">
            {Object.entries(score.signals).map(([key, sig]) => {
              const bar = Math.min(100, Math.max(0, sig.value));
              const barColor = bar >= 80 ? 'bg-emerald-500' : bar >= 60 ? 'bg-blue-500' : bar >= 40 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div key={key} className="text-xs">
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-700">{SIGNAL_LABELS[key] || key}</span>
                    <span className="text-slate-500 font-medium">{sig.value}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${bar}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {score.remediation && score.remediation.length > 0 && (
            <div className="pt-3 border-t border-slate-100" data-testid="trust-score-remediation">
              <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <TrendingUp size={12} className="text-slate-500" /> How to improve:
              </p>
              <ul className="space-y-1 text-xs text-slate-600">
                {score.remediation.map((tip, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <AlertCircle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
